import { useEffect } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import {
  UpgradeDialog,
  Button,
  List,
  CheckIcon,
  Image,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Product,
  Client,
} from '@buffer-mono/tracking-plan'

import {
  useAccount,
  useOneBufferBilling,
} from '~publish/contexts/AccountContext'
import {
  SEGMENT_NAMES,
  SERVICE_INSTAGRAM,
  SERVICE_LINKEDIN,
} from '~publish/legacy/constants'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { actions as modalsActions } from '~publish/legacy/modals'

import styles from './FirstCommentPaywallModal.module.css'

export const FirstCommentPaywallModal = ({
  service,
}: {
  service: string
}): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { account } = useAccount()
  const currentOrganization = account?.currentOrganization
  const organizationId = currentOrganization?.id ?? ''
  const billing = useOneBufferBilling()
  const canStartTrial = billing?.canStartTrial
  const openGlobalModal = useOpenGlobalModal()

  const closeModal = (): void => {
    dispatch(modalsActions.hideInstagramFirstCommentPaywallModal())
  }

  let ctaForUpgradePath = ''
  if (service === SERVICE_INSTAGRAM) {
    ctaForUpgradePath = SEGMENT_NAMES.FIRST_COMMENT_PAYWALL_UPGRADE_INSTAGRAM
  }
  if (service === SERVICE_LINKEDIN) {
    ctaForUpgradePath = SEGMENT_NAMES.FIRST_COMMENT_PAYWALL_UPGRADE_LINKEDIN
  }

  const cta = ctaForUpgradePath
  const upgradePathName = `firstComment${
    service === SERVICE_LINKEDIN ? 'linkedin' : ''
  }-${canStartTrial ? 'trial' : 'upgrade'}`

  useEffect(() => {
    const eventData = {
      cta,
      product: Product.Publish,
      upgradePathName,
      organizationId,
      clientName: Client.PublishWeb,
    }
    BufferTracker.upgradePathViewed(eventData)
  }, [cta, upgradePathName, organizationId])

  const handleUpgradeButtonClick = (): void => {
    closeModal()
    openGlobalModal(MODALS.planSelector, {
      cta,
      upgradePathName,
    })
  }

  return (
    <UpgradeDialog open={true} onOpenChange={closeModal}>
      <UpgradeDialog.Content size="xlarge" className={styles.dialogContent}>
        <UpgradeDialog.TextContent>
          <UpgradeDialog.Title>
            {t('first-comment-paywall.title')}
          </UpgradeDialog.Title>
          <List className={styles.featureList}>
            <List.Item icon={CheckIcon}>
              {t('first-comment-paywall.features.0')}
            </List.Item>
            <List.Item icon={CheckIcon}>
              {t('first-comment-paywall.features.1')}
            </List.Item>
            <List.Item icon={CheckIcon}>
              {t('first-comment-paywall.features.2')}
            </List.Item>
            <List.Item icon={CheckIcon}>
              {t('first-comment-paywall.features.3')}
            </List.Item>
          </List>
          <UpgradeDialog.Actions>
            <Button
              variant="primary"
              size="large"
              onClick={handleUpgradeButtonClick}
            >
              {t('first-comment-paywall.buttonText')}
            </Button>
          </UpgradeDialog.Actions>
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image
            src="https://buffer-publish.s3.amazonaws.com/images/first-comment-upgrade_2x.png"
            alt="upgrade for first comment scheduling"
            className={styles.upgradeImage}
            placeholder="https://buffer-publish.s3.amazonaws.com/images/first-comment-upgrade.png"
          />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}
